import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { imgSrc } from "@/lib/api";
import { getSlideDurationMs } from "@/lib/heroSlider";
import VideoPlayer from "@/components/VideoPlayer";

export default function HeroMediaSlider({ slides = [], fallbackUrl, className = "", background = false }) {
  const items = useMemo(() => {
    const configured = slides
      .filter((item) => item && item.url)
      .map((item, index) => ({ ...item, type: item.type === "video" ? "video" : "image", key: `${item.type || "image"}-${item.url}-${index}` }));
    return configured.length ? configured : [{ type: "image", url: fallbackUrl, alt: "Hero NAWADAH by NDH Official", key: `fallback-${fallbackUrl}` }];
  }, [slides, fallbackUrl]);
  const [active, setActive] = useState(0);

  useEffect(() => {
    setActive((current) => Math.min(current, items.length - 1));
  }, [items.length]);

  useEffect(() => {
    if (items.length < 2) return undefined;
    const duration = getSlideDurationMs(items[active] || items[0]);
    if (!duration) return undefined;
    const timer = window.setTimeout(() => setActive((currentIndex) => (currentIndex + 1) % items.length), duration);
    return () => window.clearTimeout(timer);
  }, [active, items]);

  const current = items[active] || items[0];
  const move = (step) => setActive((index) => (index + step + items.length) % items.length);
  const mediaClass = `absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${background ? "pointer-events-none" : ""}`;

  return (
    <div className={`relative overflow-hidden bg-black ${className}`} data-testid="hero-media-slider">
      {current.type === "video" ? (
        <VideoPlayer
          key={current.key}
          url={current.url}
          title={current.alt || `Video hero ${active + 1}`}
          poster={current.poster ? imgSrc(current.poster) : ""}
          className={mediaClass}
          autoPlay
          muted
          controls={!background}
          onError={() => move(1)}
        />
      ) : (
        <img key={current.key} src={imgSrc(current.url)} alt={current.alt || "Hero NAWADAH by NDH Official"} className={mediaClass} data-testid="hero-image" />
      )}

      {items.length > 1 && (
        <>
          {!background && (
            <>
              <button type="button" onClick={() => move(-1)} aria-label="Slide sebelumnya" className="absolute left-3 top-1/2 z-20 -translate-y-1/2 rounded-full bg-black/55 p-2 text-white hover:bg-black/75">
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button type="button" onClick={() => move(1)} aria-label="Slide berikutnya" className="absolute right-3 top-1/2 z-20 -translate-y-1/2 rounded-full bg-black/55 p-2 text-white hover:bg-black/75">
                <ChevronRight className="h-5 w-5" />
              </button>
            </>
          )}
          <div className={`absolute z-20 flex gap-2 ${background ? "bottom-5 right-5" : "bottom-4 left-1/2 -translate-x-1/2"}`} data-testid="hero-slide-dots">
            {items.map((item, index) => (
              <button
                type="button"
                key={item.key}
                onClick={() => setActive(index)}
                aria-label={`Tampilkan slide ${index + 1}`}
                className={`h-2 rounded-full transition-all ${index === active ? "w-7 bg-gold" : "w-2 bg-white/60 hover:bg-white"}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
